/// <reference types="google.maps" />
import { AfterViewInit, Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { OrderPrestadorService } from 'src/app/services/order-prestador.service';
import { MAP_ID } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { UsuarioService } from 'src/app/services/usuario.service';

@Component({
    selector: 'app-zona-geografica',
    templateUrl: './zona-geografica.component.html',
    styleUrls: ['./zona-geografica.component.css'],
    standalone: false
})
export class ZonaGeograficaComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('divMap') divMap!: ElementRef<HTMLElement>;
  @ViewChild('inputPlaces') inputPlaces!: ElementRef<HTMLInputElement>;

  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);
  private _orderPrestadorService = inject(OrderPrestadorService);
  private _usuarioService = inject(UsuarioService);
  private _router = inject(Router);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);

  // ------- estado -------
  error = signal<string>('');
  loadingGlobal = computed(() => this._loadingService.isLoading());

  // loading local (solo para overlay del mapa / geoloc)
  loadingLocal = signal<boolean>(false);

  // tooltip ayuda
  helpOpen = signal<boolean>(false);
  helpText = signal<string>('Seleccioná la dirección o ubicación para avanzar.');

  // defaults
  n_latitud = signal<number>(-34.75989677508757);
  n_longitud = signal<number>(-58.406636762250805);
  x_zona = signal<string>('');

 map: any;
  private autocomplete?: google.maps.places.Autocomplete;
  private marker?: any;

  // Form
  zonaGeografica = this._fb.nonNullable.group({
    direccion: ['', Validators.required],
  });

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        // si quedó guest -> sign-in
        if (this._authService.session() === 'guest') {
          this._router.navigate(['/sign-in']);
          return;
        }
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this._router.navigate(['/sign-in']);
      }
    });
  }

  async ngAfterViewInit(): Promise<void> {
    await this._loadingService.run(async () => {
      try {
        // Importante: esperar Google Maps (web + capacitor)
        await this.waitForGoogleMaps();
      } catch (e: any) {
        console.error(e);
        this.error.set('No se pudo cargar Google Maps. Revisá la API Key / restricciones.');
        return;
      }

      // tomar lo que haya en la orden
      const op = this._orderPrestadorService.ordenPrestador;

      this.n_latitud.set(op.n_latitud ?? this.n_latitud());
      this.n_longitud.set(op.n_longitud ?? this.n_longitud());
      this.x_zona.set(op.x_zona ?? '');

      // mapa base
      await this.cargarMapa(this.n_latitud(), this.n_longitud());

      // si ya hay zona guardada
      if ((op.x_zona ?? '').trim() !== '') {
        this.zonaGeografica.patchValue({ direccion: op.x_zona ?? '' });
        await this.cargarAutocomplete();
        return;
      }

      // si no hay zona: intentar ubicar actual
      await this.ubicacionActual(); // local loader
      await this.cargarAutocomplete();
    });
  }

  ngOnDestroy(): void {
    this.autocomplete = undefined;
    this.map = undefined;
    this.marker = undefined;
  }

  // ---------------------------
  // Google Maps load helper
  // ---------------------------
  private async waitForGoogleMaps(timeoutMs = 12000): Promise<void> {
    const start = Date.now();
    while (!(window as any).google?.maps?.importLibrary) {
      if (Date.now() - start > timeoutMs) {
        throw new Error('Google Maps no cargó a tiempo.');
      }
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  // ---------------------------
  // Ubicación actual + geocode
  // ---------------------------
  async ubicacionActual(): Promise<void> {
    this.error.set('');
    this.loadingLocal.set(true);

    try {
      const position = await this._usuarioService.getCurrentPosition();
      const { latitude, longitude } = position.coords;

      // reverse geocode -> dirección
      try {
        const { Geocoder } = (await google.maps.importLibrary('geocoding')) as google.maps.GeocodingLibrary;
        const geocoder = new Geocoder();
        const response = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });

        if (response.results?.[0]) {
          const direccion = response.results[0].formatted_address ?? '';
          this.zonaGeografica.patchValue({ direccion });
          this.x_zona.set(direccion);
        }
      } catch (e) {
        console.warn('⚠️ No se pudo geocodificar:', e);
        // no bloquea
      }

      this.n_latitud.set(latitude);
      this.n_longitud.set(longitude);

      await this.cargarMapa(latitude, longitude);
    } catch (e: any) {
      console.error('❌ Error al obtener ubicación:', e);
      if (e?.code === 1) {
        this.error.set('Permiso de ubicación denegado. Activá la ubicación en la configuración del dispositivo.');
      } else {
        this.error.set('No se pudo obtener tu ubicación. Podés escribir la dirección manualmente.');
      }
    } finally {
      this.loadingLocal.set(false);
    }
  }

  // ---------------------------
  // Autocomplete
  // ---------------------------
  private async cargarAutocomplete(): Promise<void> {
    await google.maps.importLibrary('places');

    if (!this.inputPlaces?.nativeElement) return;
    if (this.autocomplete) return;

    this.autocomplete = new google.maps.places.Autocomplete(this.inputPlaces.nativeElement, {
      componentRestrictions: { country: ['AR'] },
      fields: ['address_components', 'geometry'],
      types: ['address'],
    });

    google.maps.event.addListener(this.autocomplete, 'place_changed', () => {
      const place: any = this.autocomplete!.getPlace();
      if (!place?.geometry?.location) return;

      let direccionTemp = '';
      let localidadTemp = '';
      let numeroTemp = '';

      (place.address_components || []).forEach((item: any) => {
        if (item.types.includes('street_number')) {
          numeroTemp = ' ' + item.long_name;
        }
        if (item.types.includes('route')) {
          direccionTemp = item.long_name + numeroTemp;
        }
        if (item.types[0] === 'locality') {
          localidadTemp = item.short_name;
          direccionTemp += ', ' + item.short_name;
        }
        if (item.types[0] === 'administrative_area_level_1' && localidadTemp !== 'CABA') {
          direccionTemp += ', ' + item.long_name;
        }
      });

      this.zonaGeografica.patchValue({ direccion: direccionTemp });
      this.x_zona.set(direccionTemp);

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      this.n_latitud.set(lat);
      this.n_longitud.set(lng);

      void this.cargarMapa(lat, lng);
    });
  }

  // ---------------------------
  // Mapa + marker único
  // ---------------------------
  async cargarMapa(latitude: number, longitude: number): Promise<void> {
    const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    const el = this.divMap?.nativeElement;
    if (!el) return;

    // 1) crear una sola vez
    if (!this.map) {
      this.map = new Map(el, {
        center: { lat: latitude, lng: longitude },
        zoom: 17,
        mapId: MAP_ID,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
      });

      this.marker = new AdvancedMarkerElement({
        map: this.map,
        position: { lat: latitude, lng: longitude },
        title: 'Ubicación',
      });

      return;
    }

    // 2) reutilizar
    this.map.setCenter({ lat: latitude, lng: longitude });
    if (this.marker) this.marker.position = { lat: latitude, lng: longitude };
  }

  // ---------------------------
  // Guardar (GLOBAL loading)
  // ---------------------------
  async saveZonaGeografica(): Promise<void> {
    this.error.set('');

    const { direccion } = this.zonaGeografica.getRawValue();

    if (!direccion.trim()) {
      this.zonaGeografica.markAllAsTouched();
      this.error.set('Debe completar la dirección.');
      return;
    }

    await this._loadingService.run(async () => {
      try {

        const zona = this.x_zona().trim() || direccion.trim();

        this._orderPrestadorService.ordenPrestador.x_zona = zona;
        this._orderPrestadorService.ordenPrestador.n_latitud = this.n_latitud();
        this._orderPrestadorService.ordenPrestador.n_longitud = this.n_longitud();

        this._router.navigate(['/fecha-turno']);

      } catch (e) {
        console.error(e);
        this.error.set('No se pudo guardar la zona geográfica.');
      }
    });
  }

  // ---------------------------
  // UI
  // ---------------------------
  openhelp(): void {
    this.helpOpen.set(!this.helpOpen());
  }

  back(): void {
    this._location.back();
  }

}